import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, TriangleAlert } from 'lucide-react';
import { Button, Checkbox, Dialog, DialogBody, DialogFooter, DialogHeader, Skeleton } from '../../../components/ui';
import { Notice, RecordAvatar } from './ui';
import { mergeRecords } from '../duplicatesApi';
import { duplicatesErrorMessage } from '../duplicatesApi';
import { useMergePreview } from '../useDuplicates';
import {
  FieldRow,
  attachmentSummary,
  attachmentTotal,
  buildFieldRows,
  defaultSurvivor,
  entityHref,
  labelAgainst,
  movedSummary,
  planFieldChoices,
  plural,
  shortDate,
  splitRows,
} from '../format';
import type { DuplicateCandidate, DuplicateSide, MergePlan, MergeResult } from '../../../types/duplicates';

type Phase = 'review' | 'confirm' | 'done';

interface MergeDialogProps {
  open: boolean;
  candidate: DuplicateCandidate;
  onClose: () => void;
  /** The pair is gone from the queue once this fires */
  onMerged?: (result: MergeResult) => void;
}

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

/** One of the two records, offered as the one that stays */
const SurvivorOption: React.FC<{
  side: DuplicateSide;
  entityType: DuplicateCandidate['entityType'];
  selected: boolean;
  onSelect: () => void;
}> = ({ side, entityType, selected, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={`flex w-full items-start gap-2.5 rounded-xl p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
      selected ? 'bg-primary-50/60 ring-1 ring-inset ring-primary-600' : 'ring-1 ring-inset ring-neutral-200 hover:bg-neutral-50'
    }`}
  >
    <RecordAvatar name={side.label} imageId={side.imageId} kind={entityType} size="sm" />
    <span className="min-w-0 flex-1">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-[13px] text-ink">{side.label}</span>
        {selected && <Check className="h-3.5 w-3.5 flex-shrink-0 text-primary-600" strokeWidth={2} />}
      </span>
      <span className="block truncate text-[12px] text-neutral-500">{side.subtitle || 'No role recorded'}</span>
      <span className="mt-1 block text-[12px] text-neutral-400">
        {attachmentTotal(side) ? attachmentSummary(side) : 'Nothing attached'} · added {shortDate(side.createdAt) || 'unknown'}
      </span>
    </span>
  </button>
);

/** One value on offer for a field */
const ValueChoice: React.FC<{
  fieldLabel: string;
  sideLabel: string;
  value: string;
  selected: boolean;
  onSelect: () => void;
}> = ({ fieldLabel, sideLabel, value, selected, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    aria-label={`${fieldLabel}: ${value || 'empty'}`}
    onClick={onSelect}
    className={`min-w-0 rounded-xl px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
      selected ? 'bg-primary-50/60 ring-1 ring-inset ring-primary-600' : 'ring-1 ring-inset ring-neutral-200 hover:bg-neutral-50'
    }`}
  >
    <span className="block text-[11px] text-neutral-400 sm:hidden">{sideLabel}</span>
    <span className={`block break-words text-[13px] ${value ? 'text-ink' : 'text-neutral-400'}`}>{value || 'Empty'}</span>
  </button>
);

/** A field the two records disagree on, or one only the other record has */
const DecisionRow: React.FC<{
  row: FieldRow;
  survivorName: string;
  mergedName: string;
  onChoose: (key: string, choice: 'survivor' | 'merged') => void;
}> = ({ row, survivorName, mergedName, onChoose }) => (
  <div className="px-4 py-3">
    <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
      <span className="text-[13px] text-neutral-500">{row.label}</span>
      {row.automatic && <span className="text-[12px] text-neutral-400">empty here — takes the other value</span>}
    </div>
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label={row.label}>
      <ValueChoice
        fieldLabel={row.label}
        sideLabel={survivorName}
        value={row.survivorValue}
        selected={row.choice === 'survivor'}
        onSelect={() => onChoose(row.key, 'survivor')}
      />
      <ValueChoice
        fieldLabel={row.label}
        sideLabel={mergedName}
        value={row.mergedValue}
        selected={row.choice === 'merged'}
        onSelect={() => onChoose(row.key, 'merged')}
      />
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* The dialog                                                          */
/* ------------------------------------------------------------------ */

/**
 * Merging is permanent, so this is built to be hard to do carelessly: the
 * record that stays is chosen on purpose and says why it was suggested, every
 * field the two records disagree on is a deliberate pick, what will move is
 * spelled out before anything happens, and the last step has to be agreed to
 * in words.
 */
export const MergeDialog: React.FC<MergeDialogProps> = ({ open, candidate, onClose, onMerged }) => {
  const suggestion = useMemo(() => defaultSurvivor(candidate), [candidate]);
  const [survivorId, setSurvivorId] = useState(suggestion.survivorId);
  const [choices, setChoices] = useState<Record<string, 'survivor' | 'merged'>>({});
  const [phase, setPhase] = useState<Phase>('review');
  const [agreed, setAgreed] = useState(false);
  const [showQuiet, setShowQuiet] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MergeResult | null>(null);

  // A fresh pair is a fresh decision — nothing carries over
  useEffect(() => {
    setSurvivorId(suggestion.survivorId);
    setChoices({});
    setPhase('review');
    setAgreed(false);
    setShowQuiet(false);
    setError(null);
    setResult(null);
  }, [candidate.id, suggestion.survivorId, open]);

  const survivor = candidate.left.id === survivorId ? candidate.left : candidate.right;
  const merged = candidate.left.id === survivorId ? candidate.right : candidate.left;
  const suggested = survivorId === suggestion.survivorId;

  const rows = useMemo(() => buildFieldRows(survivor, merged, choices), [survivor, merged, choices]);
  const { decisions, quiet } = useMemo(() => splitRows(rows), [rows]);
  const taken = rows.filter((row) => row.choice === 'merged' && row.mergedValue);
  // Same-named records need something else to tell them apart in a sentence
  const mergedName = labelAgainst(merged, survivor);
  const takenSentence = taken.length
    ? `${plural(taken.length, 'field')} ${taken.length === 1 ? 'takes' : 'take'} ${mergedName}'s value.`
    : `No fields on ${survivor.label} change.`;

  const plan: MergePlan = useMemo(
    () => ({
      entityType: candidate.entityType,
      survivorId: survivor.id,
      mergedId: merged.id,
      fieldChoices: planFieldChoices(rows),
    }),
    [candidate.entityType, survivor.id, merged.id, rows],
  );

  const preview = useMergePreview(open && phase !== 'done' ? plan : null);
  const moved = preview.preview?.moved ?? merged.attachments;
  const warnings = preview.preview?.warnings ?? [];
  // Never let anyone merge without having been shown what it does
  const blind = !preview.preview && (preview.loading || !!preview.error);

  const choose = useCallback((key: string, choice: 'survivor' | 'merged') => {
    setChoices((current) => ({ ...current, [key]: choice }));
  }, []);

  const swapTo = useCallback((id: string) => {
    setSurvivorId(id);
    // The roles have swapped; every field choice made before means the opposite now
    setChoices({});
  }, []);

  const confirm = useCallback(async () => {
    setMerging(true);
    setError(null);
    try {
      const done = await mergeRecords(plan);
      setResult(done);
      setPhase('done');
      onMerged?.(done);
    } catch (err) {
      setError(duplicatesErrorMessage(err));
    } finally {
      setMerging(false);
    }
  }, [plan, onMerged]);

  const quietLabel = quiet.every((row) => row.state === 'same')
    ? `${quiet.length} ${quiet.length === 1 ? 'field agrees' : 'fields agree'}`
    : `${plural(quiet.length, 'field')} unchanged`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy="merge-dialog-title"
      handle
      surface="white"
      className="h-[92dvh] sm:h-auto sm:max-h-[86vh] sm:max-w-2xl"
    >
      <DialogHeader
        id="merge-dialog-title"
        eyebrow={phase === 'done' ? undefined : 'Permanent'}
        title={phase === 'done' ? 'Merged' : phase === 'confirm' ? 'Confirm merge' : 'Merge records'}
        onClose={onClose}
      />

      {phase === 'done' && result ? (
        <>
          <DialogBody className="space-y-3">
            <p className="text-[13px] text-ink">
              {result.mergedLabel} is now part of {survivor.label} — {movedSummary(result.moved).toLowerCase()} moved across.
            </p>
            {Object.keys(result.fieldChoices || {}).length > 0 && (
              <p className="text-[12px] text-neutral-500">
                {plural(Object.keys(result.fieldChoices).length, 'field')} took the other record's value.
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Link
              to={entityHref(candidate.entityType, result.survivorId)}
              className="inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-600 px-4 text-[13px] font-medium text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
            >
              Open {survivor.label}
              <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
            </Link>
          </DialogFooter>
        </>
      ) : phase === 'confirm' ? (
        <>
          <DialogBody className="space-y-3">
            <div className="flex items-center gap-2 text-[13px] text-ink">
              <span className="min-w-0 truncate">{mergedName}</span>
              <ArrowRight className="h-4 w-4 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />
              <span className="min-w-0 truncate">{survivor.label}</span>
            </div>
            <ul className="space-y-1 text-[13px] text-neutral-600">
              <li>{movedSummary(moved)} move to {survivor.label}.</li>
              <li>{takenSentence}</li>
              <li>{mergedName} stops existing. This cannot be undone.</li>
            </ul>
            {warnings.map((warning) => (
              <Notice key={warning} tone="caution">
                <span className="inline-flex items-start gap-2">
                  <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                  {warning}
                </span>
              </Notice>
            ))}
            {error && <Notice tone="error">{error}</Notice>}
            <Checkbox
              id="merge-understood"
              checked={agreed}
              onChange={(event) => setAgreed(event.target.checked)}
              label="I understand this cannot be undone"
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPhase('review')} disabled={merging}>
              Back
            </Button>
            <Button variant="primary" onClick={confirm} disabled={!agreed || merging} loading={merging}>
              Merge permanently
            </Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <DialogBody className="space-y-5">
            {/* Which record stays */}
            <section>
              <h3 className="mb-2 text-[13px] text-neutral-500">Which record stays</h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Which record stays">
                <SurvivorOption
                  side={candidate.left}
                  entityType={candidate.entityType}
                  selected={survivorId === candidate.left.id}
                  onSelect={() => swapTo(candidate.left.id)}
                />
                <SurvivorOption
                  side={candidate.right}
                  entityType={candidate.entityType}
                  selected={survivorId === candidate.right.id}
                  onSelect={() => swapTo(candidate.right.id)}
                />
              </div>
              <p className="mt-2 text-[12px] text-neutral-400">
                {suggested ? `Suggested: ${suggestion.reason.toLowerCase()}` : 'You changed the suggested keeper'}
              </p>
            </section>

            {/* What moves */}
            <section>
              <h3 className="mb-2 text-[13px] text-neutral-500">What moves</h3>
              {preview.loading && !preview.preview ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <p className="text-[13px] text-ink">
                  {movedSummary(moved)} move from {mergedName} to {survivor.label}.
                </p>
              )}
              {preview.error && (
                <Notice tone="error" className="mt-2">
                  {preview.unavailable ? 'This server cannot preview merges yet' : preview.error}
                </Notice>
              )}
              {warnings.map((warning) => (
                <Notice key={warning} tone="caution" className="mt-2">
                  <span className="inline-flex items-start gap-2">
                    <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                    {warning}
                  </span>
                </Notice>
              ))}
            </section>

            {/* Fields */}
            <section>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <h3 className="text-[13px] text-neutral-500">Fields</h3>
                <span className="text-[12px] text-neutral-400">
                  {decisions.length ? plural(decisions.length, 'to decide', 'to decide') : 'Nothing to decide'}
                </span>
              </div>
              <div className="overflow-hidden rounded-2xl border border-neutral-200">
                {/* Column names once, at the top, on screens wide enough to hold two columns */}
                {decisions.length > 0 && (
                  <div className="hidden grid-cols-2 gap-2 border-b border-neutral-200/70 bg-neutral-50/60 px-4 py-2 sm:grid">
                    <span className="truncate text-[12px] text-neutral-500">{survivor.label} · staying</span>
                    <span className="truncate text-[12px] text-neutral-500">{merged.label} · folding in</span>
                  </div>
                )}
                <div className="divide-y divide-neutral-200/70">
                  {decisions.map((row) => (
                    <DecisionRow
                      key={row.key}
                      row={row}
                      survivorName={`${survivor.label} · staying`}
                      mergedName={`${merged.label} · folding in`}
                      onChoose={choose}
                    />
                  ))}
                </div>
                {quiet.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowQuiet((value) => !value)}
                      aria-expanded={showQuiet}
                      className={`flex w-full items-center justify-between gap-2 px-4 py-2.5 text-[13px] text-neutral-500 transition-colors hover:bg-neutral-50 ${
                        decisions.length ? 'border-t border-neutral-200/70' : ''
                      }`}
                    >
                      {quietLabel}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${showQuiet ? 'rotate-180' : ''}`}
                        strokeWidth={1.75}
                      />
                    </button>
                    {showQuiet && (
                      <ul className="divide-y divide-neutral-200/70 border-t border-neutral-200/70">
                        {quiet.map((row) => (
                          <li key={row.key} className="flex items-baseline justify-between gap-3 px-4 py-2">
                            <span className="flex-shrink-0 text-[12px] text-neutral-500">{row.label}</span>
                            <span className="min-w-0 break-words text-right text-[13px] text-ink">
                              {row.survivorValue || <span className="text-neutral-400">Empty</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </section>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="dark"
              onClick={() => setPhase('confirm')}
              disabled={blind}
              title={blind ? 'Waiting to see what the merge would do' : undefined}
            >
              Review merge
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
